import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import { EmptyState } from '@/components/molecules/EmptyState';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import SomethingHappened from '@/components/SomethingHappened';
import { usePermissions } from '@/contexts/auth';
import { useGetBioscopeImages } from '@/domain/bioscopeImages';
import type { BioscopeDetail } from '@/domain/bioscopes';
import { useCommitImageFeedback } from '@/domain/imageFeedback';
import { bioscopeImageBioscopeMapper } from '@/domain/images';
import { useImageState } from '@/hooks/image-state';
import useQueryParams from '@/hooks/useQueryParams';

import styles from './BioscopeImageCardContainer.module.scss';

interface BioscopeImageCardContainerProps {
    bioscope: BioscopeDetail;
}

export default function BioscopeImageCardContainer(props: BioscopeImageCardContainerProps) {
    const { bioscope } = props;
    const { t } = useTranslation();
    const [queryParams] = useQueryParams();
    const permalinkImageId =
        typeof queryParams.bioscopeImageId === 'string' ? queryParams.bioscopeImageId : undefined;

    const { images, days, isLoading, isPreviousData, isError, isFetching, refetch, onDayChange } =
        useGetBioscopeImages(bioscope.id, permalinkImageId);

    const { isOptoScaleAdmin } = usePermissions();

    const onCommitImageFeedback = useCommitImageFeedback({ bioscopeId: bioscope?.id });

    const bioscopeImageState = useImageState({
        timezone: bioscope?.lastLocation?.cage?.location?.timezone ?? 'Europe/Oslo',
        loading: isLoading && images.length === 0,
        isFetching: isPreviousData,
        data: images,
        dayswithimages: days,
        onDayChange,
        mapper: useMemo(() => bioscopeImageBioscopeMapper(bioscope.id), [bioscope.id]),
        queryParam: 'bioscopeImageId',
    });

    const hasImages = bioscopeImageState.images.length > 0;
    const showInitialLoader = bioscopeImageState.loading && !hasImages;
    const showEmpty = !bioscopeImageState.loading && !hasImages;

    if (isError) {
        return <SomethingHappened onRetry={refetch} isRetrying={isFetching} />;
    }

    return (
        <div className={styles.images}>
            {showInitialLoader && <Spinner size="lg" />}
            {showEmpty && <EmptyState title={t('common.labels.atTheMomentWeDo')} />}
            {hasImages && (
                <ImageViewer
                    key={`bioscope-${bioscope.id}`}
                    variant="bioscope"
                    isColor={false}
                    options={{ magnifierEnabled: false, showHelp: false }}
                    isOptoScaleAdmin={isOptoScaleAdmin}
                    onCommitImageFeedback={onCommitImageFeedback}
                    images={bioscopeImageState.images}
                    daysWithImages={bioscopeImageState.dayswithimages}
                    onDayChange={bioscopeImageState.onAfterTimelineChangeHandler}
                    initialImageIndex={bioscopeImageState.initialImageIndex}
                    isFetching={bioscopeImageState.isFetching}
                />
            )}
        </div>
    );
}
